import { DetailsPanel } from '@/components/controls';
import { useNichePersonImage, useSubject } from '@/hooks';
import {
  ElectricBolt as ElectricBoltIcon,
  Facebook as FacebookIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardHeader,
  CardMedia,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import {
  DesktopClassic as DesktopClassicIcon,
  Pistol as PistolIcon,
} from 'mdi-material-ui';
import { FlagAvatar } from './FlagAvatar';

export function PrisonRecall({ recall, onOptionClick }) {
  const subjectType = 'PRISON_RECALL';
  const { data: imageSrc } = useNichePersonImage(recall?.personId);
  const { data: subject } = useSubject(subjectType, recall?.id, {
    type: subjectType,
    personId: recall?.id,
  });

  return (
    <Card variant="outlined" sx={{ minWidth: 240 }}>
      <CardHeader
        avatar={<FlagAvatar code="PRC" />}
        title={[recall?.forenames, recall?.surname].join(' ').toLowerCase()}
        sx={{ textTransform: 'capitalize' }}
      />
      {imageSrc ? (
        <CardMedia
          component="img"
          sx={{ minHeight: 240, width: '100%' }}
          image={imageSrc}
          alt="Photo of recall subject"
        />
      ) : (
        <Box
          sx={(theme) => ({
            height: 240,
            width: '100%',
            bgcolor: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.300',
          })}
        />
      )}
      <Stack spacing={1} sx={{ p: 1 }}>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            variant="body3"
            color="textSecondary"
            sx={{ flexGrow: 1 }}
          >
            {recall?.occurrenceNumber}
          </Typography>
        </Stack>
        {recall?.outstandingForOtherMatters && (
          <Stack direction="row" spacing={1}>
            <Chip label="Outstanding for other matters" size="small" />
          </Stack>
        )}
        <DetailsPanel
          entries={[
            {
              label: 'Last log entry',
              value: recall?.lastLogEntryTime
                ? format(recall.lastLogEntryTime, 'dd/MM/yyyy HH:mm')
                : '',
            },
          ]}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'flex-end', alignItems: 'center' }}
        >
          <Tooltip
            title={
              subject?.pncCirculated
                ? 'Circulated on PNC'
                : 'Not circulated on PNC'
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                recall?.id,
                subject?.code,
                'pncCirculated',
              )}
              color={subject?.pncCirculated ? 'primary' : 'default'}
            >
              <DesktopClassicIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.socialMediaCirculated
                ? 'Circulated on social media'
                : 'Not circulated on social media'
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                recall?.id,
                subject?.code,
                'socialMediaCirculated',
              )}
              color={subject?.socialMediaCirculated ? 'primary' : 'default'}
            >
              <FacebookIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.taser ? 'Taser officers only' : 'No Taser authority'
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                recall?.id,
                subject?.code,
                'taser',
              )}
              color={subject?.taser ? 'primary' : 'default'}
            >
              <ElectricBoltIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.firearms
                ? 'Firearms officers only'
                : 'No Firearms authority'
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                recall?.id,
                subject?.code,
                'firearms',
              )}
              color={subject?.firearms ? 'primary' : 'default'}
            >
              <PistolIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
}
